import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { triggerTextFileDownload } from '@/lib/trigger-download';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  ChevronLeft,
  Download,
  Search,
  FileText,
  Mail,
  Phone,
  Calendar,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { EventsPageShell } from '../components/EventsPageShell';

interface Registration {
  id: string;
  status: string;
  name: string;
  email: string;
  phone: string | null;
  formData: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  userId: string;
  ticketId: string;
  ticketName: string;
  price: number;
  ticketSchema?: Array<{
    id: string;
    type: string;
    label: string;
  }>;
}

export default function EventRegistrationsPage() {
  const { communityId, eventId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const { data, error, isLoading, mutate } = useSWR(
    eventId ? `/api/events/${eventId}/registrations` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const [isUpdating, setIsUpdating] = useState(false);

  const handleToggleRegistration = async (checked: boolean) => {
    if (!eventId) return;
    setIsUpdating(true);
    try {
      await axiosInstance.patch(`/api/events/${eventId}`, {
        isRegistrationClosed: checked,
      });
      mutate({ ...data, isRegistrationClosed: checked });
      toast.success(checked ? 'Registrations closed' : 'Registrations reopened');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update registration status');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) return <div className="p-8 text-center">Loading registrations...</div>;
  if (error) return <div className="p-8 text-center text-destructive">Error: {error.message}</div>;

  const registrations: Registration[] = data?.registrations || [];

  const filteredRegistrations = registrations.filter(
    (reg) =>
      reg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.ticketName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return (
          <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20 border-green-500/20">
            Confirmed
          </Badge>
        );
      case 'CANCELLED':
        return <Badge variant="destructive">Cancelled</Badge>;
      case 'ATTENDED':
        return (
          <Badge className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border-blue-500/20">
            Attended
          </Badge>
        );
      case 'PENDING':
        return <Badge variant="outline">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const downloadCSV = () => {
    if (registrations.length === 0) return;

    // Gather all unique form data keys to use as dynamic headers
    const formKeys = new Set<string>();
    registrations.forEach((reg) => {
      if (reg.formData) {
        Object.keys(reg.formData).forEach((key) => {
          const schemaItem = reg.ticketSchema?.find((s) => s.id === key);
          formKeys.add(schemaItem?.label || key.replace(/_/g, ' '));
        });
      }
    });

    const formHeaders = Array.from(formKeys);
    const headers = ['Name', 'Email', 'Phone', 'Ticket', 'Status', 'Date', ...formHeaders];

    const csvContent = [
      headers.join(','),
      ...registrations.map((reg) => {
        // Map the registration's form data to its labels to align with headers
        const mappedFormData: Record<string, string> = {};
        if (reg.formData) {
          Object.entries(reg.formData).forEach(([key, value]) => {
            const schemaItem = reg.ticketSchema?.find((s) => s.id === key);
            const displayKey = schemaItem?.label || key.replace(/_/g, ' ');
            mappedFormData[displayKey] = Array.isArray(value)
              ? value.join(', ')
              : String(value || '');
          });
        }

        return [
          `"${reg.name.replace(/"/g, '""')}"`,
          `"${reg.email}"`,
          `"${reg.phone || ''}"`,
          `"${reg.ticketName}"`,
          `"${reg.status}"`,
          `"${format(new Date(reg.createdAt), 'yyyy-MM-dd HH:mm')}"`,
          ...formHeaders.map((header) => {
            const val = mappedFormData[header];
            return val ? `"${val.replace(/"/g, '""')}"` : '""';
          }),
        ].join(',');
      }),
    ].join('\n');

    triggerTextFileDownload(`registrations_event_${eventId}.csv`, csvContent);
  };

  return (
    <EventsPageShell maxWidthClassName="max-w-7xl">
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <Link
              to={`/${communityId}/events/${data?.id || eventId}/manage`}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center mb-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Management Hub
            </Link>
            <h1 className="text-3xl font-bold">Event Registrations</h1>
            <p className="text-muted-foreground flex items-center">
              <Users className="w-4 h-4 mr-2" />
              {registrations.length} Total Registrations
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2 bg-muted/30 px-3 py-1.5 rounded-lg border">
              <Switch
                id="registration-closed"
                checked={data?.isRegistrationClosed || false}
                onCheckedChange={handleToggleRegistration}
                disabled={isUpdating}
              />
              <Label
                htmlFor="registration-closed"
                className="text-sm font-medium cursor-pointer flex flex-col"
              >
                <span>
                  {data?.isRegistrationClosed ? 'Registrations Closed' : 'Registrations Open'}
                </span>
                <span className="text-[10px] text-muted-foreground font-normal">
                  Quickly toggle availability
                </span>
              </Label>
            </div>
            <Button variant="outline" onClick={downloadCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <Card className="border-none shadow-none sm:border-solid sm:shadow-sm bg-transparent sm:bg-card">
          <CardHeader className="px-0 pt-0 sm:px-6 sm:pt-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle>Attendees</CardTitle>
                <CardDescription>
                  Manage and view registration data for all attendees.
                </CardDescription>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search attendees..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0 sm:px-6">
            <div className="hidden md:block rounded-none sm:rounded-2xl border-x-0 border-y sm:border bg-card/50 overflow-hidden">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead className="font-bold">Attendee</TableHead>
                    <TableHead className="font-bold">Ticket</TableHead>
                    <TableHead className="font-bold">Status</TableHead>
                    <TableHead className="font-bold">Date</TableHead>
                    <TableHead className="text-right font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRegistrations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        No registrations found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRegistrations.map((reg) => (
                      <TableRow key={reg.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{reg.name}</span>
                            <span className="text-xs text-muted-foreground">{reg.email}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDisplayPhone(reg.phone)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{reg.ticketName}</span>
                            <span className="text-xs text-muted-foreground">₹{reg.price}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(reg.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center text-xs font-medium text-muted-foreground bg-muted/30 w-fit px-2 py-1 rounded-md">
                            <Calendar className="w-3 h-3 mr-1" />
                            {format(new Date(reg.createdAt), 'MMM d, yyyy')}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <FileText className="w-4 h-4 mr-2" />
                                View Details
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto p-0 border-none shadow-2xl">
                              <div className="bg-primary/5 p-6 border-b border-primary/10">
                                <DialogHeader>
                                  <DialogTitle className="text-2xl font-bold">
                                    Registration Details
                                  </DialogTitle>
                                  <DialogDescription className="text-muted-foreground">
                                    Attendee:{' '}
                                    <span className="text-foreground font-medium">{reg.name}</span>
                                  </DialogDescription>
                                </DialogHeader>
                              </div>
                              <div className="p-6 space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground uppercase font-semibold">
                                      Status
                                    </p>
                                    {getStatusBadge(reg.status)}
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground uppercase font-semibold">
                                      Registration Date
                                    </p>
                                    <p className="text-sm">
                                      {format(new Date(reg.createdAt), 'MMM d, yyyy HH:mm')}
                                    </p>
                                  </div>
                                </div>

                                <div className="space-y-4">
                                  <h4 className="text-sm font-semibold border-b pb-2">
                                    Contact Information
                                  </h4>
                                  <div className="space-y-2">
                                    <div className="flex items-center text-sm">
                                      <Mail className="w-4 h-4 mr-2 text-muted-foreground" />
                                      {reg.email}
                                    </div>
                                    {reg.phone && (
                                      <div className="flex items-center text-sm">
                                        <Phone className="w-4 h-4 mr-2 text-muted-foreground" />
                                        {formatDisplayPhone(reg.phone)}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {Object.keys(reg.formData).length > 0 && (
                                  <div className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-primary/40"></span>
                                      Form Responses
                                    </h4>
                                    <div className="grid gap-3">
                                      {Object.entries(reg.formData).map(([key, value]) => {
                                        const schemaItem = reg.ticketSchema?.find(
                                          (s) => s.id === key
                                        );
                                        const label = schemaItem?.label || key.replace(/_/g, ' ');
                                        const isImage =
                                          schemaItem?.type === 'IMAGE' ||
                                          (typeof value === 'string' &&
                                            value.startsWith('http') &&
                                            (value.includes('.png') ||
                                              value.includes('.jpg') ||
                                              value.includes('.jpeg') ||
                                              value.includes('.gif') ||
                                              value.includes('.webp')));

                                        return (
                                          <div key={key} className="space-y-1.5">
                                            <p className="text-xs font-semibold text-muted-foreground capitalize">
                                              {label}
                                            </p>
                                            <div className="bg-muted/50 p-3 rounded-xl border border-border/50 shadow-inner overflow-hidden">
                                              {isImage && typeof value === 'string' ? (
                                                <a
                                                  href={value}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  <img
                                                    src={value}
                                                    alt={label}
                                                    className="max-w-full max-h-48 object-contain rounded-lg border bg-background/50"
                                                  />
                                                </a>
                                              ) : (
                                                <p className="text-sm break-words">
                                                  {String(value)}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards View */}
            <div className="grid grid-cols-1 gap-4 md:hidden p-4 sm:p-0">
              {filteredRegistrations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-xl bg-card">
                  No registrations found.
                </div>
              ) : (
                filteredRegistrations.map((reg) => (
                  <div
                    key={`mobile-${reg.id}`}
                    className="flex flex-col p-4 border rounded-xl bg-card shadow-sm gap-4"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold truncate text-lg">{reg.name}</span>
                        <span className="text-sm text-muted-foreground truncate flex items-center mt-1">
                          <Mail className="w-3 h-3 mr-1.5" />
                          {reg.email}
                        </span>
                        {reg.phone && (
                          <span className="text-sm text-muted-foreground mt-0.5 flex items-center">
                            <Phone className="w-3 h-3 mr-1.5" />
                            {formatDisplayPhone(reg.phone)}
                          </span>
                        )}
                      </div>
                      <div>{getStatusBadge(reg.status)}</div>
                    </div>

                    <div className="flex flex-col gap-1.5 bg-muted/40 p-3 rounded-lg border border-border/50">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">{reg.ticketName}</span>
                        <span className="text-sm font-semibold text-primary">₹{reg.price}</span>
                      </div>
                      <div className="flex items-center text-xs font-medium text-muted-foreground mt-0.5">
                        <Calendar className="w-3 h-3 mr-1.5" />
                        {format(new Date(reg.createdAt), 'MMM d, yyyy HH:mm')}
                      </div>
                    </div>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="secondary" className="w-full">
                          <FileText className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto p-0 border-none shadow-2xl">
                        <div className="bg-primary/5 p-6 border-b border-primary/10">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold">
                              Registration Details
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                              Attendee:{' '}
                              <span className="text-foreground font-medium">{reg.name}</span>
                            </DialogDescription>
                          </DialogHeader>
                        </div>
                        <div className="p-6 space-y-6">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground uppercase font-semibold">
                                Status
                              </p>
                              {getStatusBadge(reg.status)}
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground uppercase font-semibold">
                                Registration Date
                              </p>
                              <p className="text-sm">
                                {format(new Date(reg.createdAt), 'MMM d, yyyy HH:mm')}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h4 className="text-sm font-semibold border-b pb-2">
                              Contact Information
                            </h4>
                            <div className="space-y-2">
                              <div className="flex items-center text-sm">
                                <Mail className="w-4 h-4 mr-2 text-muted-foreground" />
                                {reg.email}
                              </div>
                              {reg.phone && (
                                <div className="flex items-center text-sm">
                                  <Phone className="w-4 h-4 mr-2 text-muted-foreground" />
                                  {formatDisplayPhone(reg.phone)}
                                </div>
                              )}
                            </div>
                          </div>

                          {Object.keys(reg.formData).length > 0 && (
                            <div className="space-y-4">
                              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-primary/40"></span>
                                Form Responses
                              </h4>
                              <div className="grid gap-3">
                                {Object.entries(reg.formData).map(([key, value]) => {
                                  const schemaItem = reg.ticketSchema?.find((s) => s.id === key);
                                  const label = schemaItem?.label || key.replace(/_/g, ' ');
                                  const isImage =
                                    schemaItem?.type === 'IMAGE' ||
                                    (typeof value === 'string' &&
                                      value.startsWith('http') &&
                                      (value.includes('.png') ||
                                        value.includes('.jpg') ||
                                        value.includes('.jpeg') ||
                                        value.includes('.gif') ||
                                        value.includes('.webp')));

                                  return (
                                    <div key={key} className="space-y-1.5">
                                      <p className="text-xs font-semibold text-muted-foreground capitalize">
                                        {label}
                                      </p>
                                      <div className="bg-muted/50 p-3 rounded-xl border border-border/50 shadow-inner overflow-hidden">
                                        {isImage && typeof value === 'string' ? (
                                          <a href={value} target="_blank" rel="noopener noreferrer">
                                            <img
                                              src={value}
                                              alt={label}
                                              className="max-w-full max-h-48 object-contain rounded-lg border bg-background/50"
                                            />
                                          </a>
                                        ) : (
                                          <p className="text-sm break-words">{String(value)}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </EventsPageShell>
  );
}
